const APP_KEYBOARD_INSET_PROPERTY = '--app-keyboard-inset-height';
const MIN_KEYBOARD_INSET_HEIGHT = 100;
const SHEET_CONTAINER_SELECTOR = '.react-modal-sheet-container';

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

function canOpenKeyboard(element: Element | null): element is HTMLElement {
  if (element instanceof HTMLInputElement) {
    return (
      !element.disabled &&
      !element.readOnly &&
      !NON_TEXT_INPUT_TYPES.has(element.type)
    );
  }

  if (element instanceof HTMLTextAreaElement) {
    return !element.disabled && !element.readOnly;
  }

  return element instanceof HTMLElement && element.isContentEditable;
}

/**
 * Keeps the app shell above a virtual keyboard without making React own the
 * viewport height. CSS remains responsible for the viewport; this bridge only
 * publishes the temporarily obscured bottom inset.
 */
export function syncAppKeyboardInset() {
  const root = document.documentElement;
  const viewport = window.visualViewport;

  const setInset = (height: number) => {
    root.style.setProperty(APP_KEYBOARD_INSET_PROPERTY, `${height}px`);
  };

  const resetInset = () => setInset(0);

  if (!viewport) {
    resetInset();
    return () => root.style.removeProperty(APP_KEYBOARD_INSET_PROPERTY);
  }

  const updateInset = () => {
    const focusedElement = document.activeElement;

    if (
      !canOpenKeyboard(focusedElement) ||
      focusedElement.closest(SHEET_CONTAINER_SELECTOR)
    ) {
      resetInset();
      return;
    }

    const obscuredBottom = Math.max(
      0,
      window.innerHeight - viewport.height - viewport.offsetTop,
    );
    const keyboardInset =
      obscuredBottom >= MIN_KEYBOARD_INSET_HEIGHT
        ? Math.round(obscuredBottom)
        : 0;

    setInset(keyboardInset);
  };

  const handleFocusIn = (event: FocusEvent) => {
    if (canOpenKeyboard(event.target as Element | null)) {
      updateInset();
    }
  };

  document.addEventListener('focusin', handleFocusIn);
  document.addEventListener('focusout', resetInset);
  viewport.addEventListener('resize', updateInset);
  viewport.addEventListener('scroll', updateInset);
  window.addEventListener('resize', updateInset);
  window.addEventListener('orientationchange', resetInset);

  updateInset();

  return () => {
    document.removeEventListener('focusin', handleFocusIn);
    document.removeEventListener('focusout', resetInset);
    viewport.removeEventListener('resize', updateInset);
    viewport.removeEventListener('scroll', updateInset);
    window.removeEventListener('resize', updateInset);
    window.removeEventListener('orientationchange', resetInset);
    root.style.removeProperty(APP_KEYBOARD_INSET_PROPERTY);
  };
}
